/* import __COLOCATED_TEMPLATE__ from './signal-filtering-options.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Intl from 'ember-intl/services/intl';
import type AnalyticsService from '@ebiz/ember-aditools-analytics/services/aditools-analytics';
import type CircuitService from '../services/circuit';
import type FilterService from '../services/filter';

interface SignalFilteringOptionsSignature {
  Element: HTMLDivElement;
}
/**
 * The main panel of the application
 *
 * @extends {Component}
 */
export default class SignalFilteringOptionsPanel extends Component<SignalFilteringOptionsSignature> {
  @service declare intl: Intl;
  @service('aditools-analytics') declare analytics: AnalyticsService;
  @service declare filter: FilterService;
  @service declare circuit: CircuitService;

  get filterTypes() {
    return [{ value: 0, display: this.intl.t('filter.type_lowpass') }];
  }
  get filterTopologies() {
    return [
      { value: 0, display: this.intl.t('filter.topology_butterworth') },
      { value: 1, display: this.intl.t('filter.topology_chebyshev') },
      { value: 2, display: this.intl.t('filter.topology_inverse_chebyshev') },
      { value: 3, display: this.intl.t('filter.topology_bessel') },
    ];
  }
  get filterOrders() {
    return [
      { value: 1, display: '1' },
      { value: 2, display: '2' },
      { value: 3, display: '3' },
      { value: 4, display: '4' },
      { value: 5, display: '5' },
      { value: 6, display: '6' },
      { value: 7, display: '7' },
    ];
  }
  get showPassbandRipple() {
    return this.filter.filterTopology === 1;
  }
  get showStopbandAtten() {
    return this.filter.filterTopology === 2;
  }
  get disableFilters() {
    const result = this.filter.displayFilters;
    // if there is a requirement to disable the external filter controls if there is an error, return result and hasInvalids
    // var hasInvalids = this.circuit.hasCriticalError;
    // return !result || hasInvalids;
    return !result;
  }
  get passbandRipple() {
    return this.filter.passbandRipple;
  }
  get stopbandAtten() {
    return this.filter.stopbandAtten;
  }

  @action onFcChange(value: number) {
    this.filter.fc = value === 0 ? 1 : value;
  }
  @action onPassbandRippleChange(event: Event) {
    if (event.target && event.target instanceof HTMLInputElement) {
      if (this.filter.passbandRipple !== Number(event.target.value)) {
        if (/^[+-]?\d*[.]?\d*$/.test(event.target.value.trim())) {
          this.filter.passbandRipple = Number(event.target.value);
        } else {
          event.target.value = this.passbandRipple.toString();
        }
      }
    }
  }
  @action onStopbandAttenChange(event: Event) {
    if (event.target && event.target instanceof HTMLInputElement) {
      if (this.filter.stopbandAtten !== Number(event.target.value)) {
        if (/^[+-]?\d*[.]?\d*$/.test(event.target.value.trim())) {
          this.filter.stopbandAtten = Number(event.target.value);
        } else {
          event.target.value = this.stopbandAtten.toString();
        }
      }
    }
  }
  @action filterClicked(value: boolean) {
    this.filter.displayFilters = value;
    this.analytics.setWebEvent('SimDDS', 'useFilter', 'filterUsed: ' + value);
  }

  @action onFilterTypeChange(value: string | number) {
    const newVal = Number(value);
    if (this.filter.filterType !== newVal) {
      this.filter.filterType = newVal;
    }
  }

  @action onFilterTopologyChange(value: string | number) {
    const newVal = Number(value);
    if (this.filter.filterTopology !== newVal) {
      this.filter.filterTopology = newVal;
    }
  }

  @action onFilterOrderChange(value: string | number) {
    const newVal = Number(value);
    if (this.filter.filterOrder !== newVal) {
      this.filter.filterOrder = newVal;
    }
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    SignalFilteringOptions: typeof SignalFilteringOptionsPanel;
  }
}
